import { tsvFormat } from 'd3-dsv'

function normalizeOpenMeteoPayload(payload) {
  const hourly = payload?.hourly
  const time = hourly?.time
  if (!Array.isArray(time)) {
    return []
  }

  const hourlyFields = Object.keys(hourly).filter((key) => key !== 'time')

  return time.map((timestamp, index) => {
    const row = {
      time:
        typeof timestamp === 'string' && timestamp.length === 16
          ? `${timestamp}:00`
          : timestamp,
    }

    hourlyFields.forEach((field) => {
      const values = hourly[field]
      row[field] = Array.isArray(values) ? values[index] ?? null : null
    })

    return row
  })
}

function normalizeCensusPayload(payload) {
  if (!Array.isArray(payload) || payload.length < 2 || !Array.isArray(payload[0])) {
    return []
  }

  const [headers, ...rows] = payload
  return rows.map((row) =>
    headers.reduce((acc, header, index) => {
      acc[header] = row?.[index] ?? null
      return acc
    }, {})
  )
}

function buildUrlFromTemplate(template, params) {
  let url = template
  for (const [key, value] of Object.entries(params)) {
    url = url.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value ?? ''))
  }
  return url
}

function normalizeWorldBank(payload) {
  const rows = Array.isArray(payload) ? payload[1] : null
  if (!Array.isArray(rows)) return []
  return rows
    .filter((r) => r?.value !== null && r?.value !== undefined)
    .map((r) => ({
      country: r.country?.value ?? '',
      country_code: r.countryiso3code ?? '',
      year: r.date ?? '',
      value: r.value ?? null,
      indicator: r.indicator?.value ?? '',
    }))
}

function normalizeUSGS(payload) {
  const features = payload?.features
  if (!Array.isArray(features)) return []
  return features.map((f) => {
    const p = f.properties ?? {}
    const [lon, lat, depth] = f.geometry?.coordinates ?? []
    return {
      time: p.time ? new Date(p.time).toISOString() : '',
      place: p.place ?? '',
      magnitude: p.mag ?? null,
      lon: lon ?? null,
      lat: lat ?? null,
      depth_km: depth ?? null,
      status: p.status ?? '',
    }
  })
}

function normalizeFrankfurter(payload) {
  const rates = payload?.rates
  if (!rates || typeof rates !== 'object') return []
  const rows = []
  for (const [date, currencies] of Object.entries(rates)) {
    if (typeof currencies !== 'object' || currencies === null) continue
    for (const [currency, rate] of Object.entries(currencies)) {
      rows.push({ date, currency, rate: typeof rate === 'number' ? rate : null })
    }
  }
  return rows
}

function normalizeTreasuryFiscalData(payload) {
  if (!Array.isArray(payload?.data)) return []

  return payload.data.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => {
        // Treasury returns monetary values and rates as strings. Converting them
        // here preserves numeric chart mappings while keeping dates and labels.
        if (/(amt|bal|rate)/.test(key) && value !== null && value !== 'null') {
          const numeric = Number(value)
          return [key, Number.isFinite(numeric) ? numeric : value]
        }
        return [key, value === 'null' ? null : value]
      })
    )
  )
}

const TREASURY_DATASETS = {
  debt: {
    endpoint: 'v2/accounting/od/debt_to_penny',
    fields: 'record_date,debt_held_public_amt,tot_pub_debt_out_amt',
    label: 'Debt to the Penny',
  },
  cash: {
    endpoint: 'v1/accounting/dts/operating_cash_balance',
    fields: 'record_date,open_today_bal',
    label: 'Treasury General Account cash balance',
    filter: 'account_type:eq:Treasury General Account (TGA) Opening Balance',
  },
  rates: {
    endpoint: 'v2/accounting/od/avg_interest_rates',
    fields: 'record_date,security_desc,avg_interest_rate_amt',
    label: 'Average interest rates',
  },
}

function buildTreasuryUrl(params) {
  const dataset = TREASURY_DATASETS[params.dataset] ?? TREASURY_DATASETS.debt
  const filters = [
    dataset.filter,
    `record_date:gte:${params.start}`,
    `record_date:lte:${params.end}`,
  ].filter(Boolean).join(',')
  const query = new URLSearchParams({
    fields: dataset.fields,
    filter: filters,
    sort: 'record_date',
    'page[size]': '5000',
  })
  return `https://api.fiscaldata.treasury.gov/services/api/fiscal_service/${dataset.endpoint}?${query}`
}

function defaultNormalizeToTsv(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return ''
  }

  return tsvFormat(rows)
}

export function resolveDataPath(payload, path) {
  if (!path || !path.trim()) return payload
  const trimmed = path.trim()
  const arrayMatch = trimmed.match(/^\[(\d+)\]$/)
  if (arrayMatch) return payload[parseInt(arrayMatch[1], 10)]
  return trimmed.split('.').reduce((obj, key) => obj?.[key], payload)
}

export function genericFlatten(data) {
  if (Array.isArray(data)) {
    if (data.length > 0 && typeof data[0] !== 'object') {
      return data.map((v, i) => ({ index: i, value: v }))
    }
    return data.filter((item) => item !== null && item !== undefined)
  }
  if (data && typeof data === 'object') {
    return Object.entries(data).map(([key, value]) => ({
      key,
      ...(value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value
        : { value }),
    }))
  }
  return []
}

const OPEN_METEO_CITIES = {
  'Berlin':        { lat: 52.52,  lon: 13.41  },
  'New York':      { lat: 40.71,  lon: -74.01 },
  'San Francisco': { lat: 37.77,  lon: -122.42 },
  'London':        { lat: 51.51,  lon: -0.13  },
  'Tokyo':         { lat: 35.68,  lon: 139.69 },
  'Sydney':        { lat: -33.87, lon: 151.21 },
}

export const API_SOURCES = [
  {
    id: 'treasury-fiscal-data',
    label: 'U.S. Treasury Fiscal Data',
    description: 'Federal debt, cash balance & interest rates',
    fmt: 'JSON',
    requiresKey: false,
    badge: 'New',
    dataPath: 'data',
    iconPaths: '<path d="M3 21h18M5 18h14M6 15h12M7 6h10l2 5H5l2-5Z"/><path d="M9 10v5M12 10v5M15 10v5"/>',
    params: [
      {
        k: 'dataset',
        label: 'Dataset',
        type: 'select',
        opts: [
          { label: 'Debt to the Penny', value: 'debt' },
          { label: 'Treasury General Account cash balance', value: 'cash' },
          { label: 'Average interest rates', value: 'rates' },
        ],
        default: 'debt',
      },
      {
        k: 'start',
        label: 'Start date',
        type: 'date',
        default: '2024-01-01',
      },
      {
        k: 'end',
        label: 'End date',
        type: 'date',
        default: 'today',
      },
    ],
    buildUrl: buildTreasuryUrl,
    defaultUrl: buildTreasuryUrl({
      dataset: 'debt',
      start: '2024-01-01',
      end: new Date().toISOString().split('T')[0],
    }),
    buildMeta: (params) => {
      const dataset = TREASURY_DATASETS[params.dataset] ?? TREASURY_DATASETS.debt
      return {
        title: `${dataset.label} (${params.start ?? ''} – ${params.end ?? ''})`,
        source: 'U.S. Treasury Fiscal Data',
      }
    },
    matchesUrl: (url) => /api\.fiscaldata\.treasury\.gov/i.test(url),
    matchesPayload: (payload) =>
      Array.isArray(payload?.data) && typeof payload?.meta?.labels === 'object',
    normalize: normalizeTreasuryFiscalData,
  },
  {
    id: 'fifa-wc-2026',
    label: 'FIFA World Cup 2026',
    description: 'Matches, events & player stats',
    fmt: 'CSV',
    requiresKey: false,
    passThroughText: true,
    iconPaths: '<circle cx="8" cy="8" r="6.5"/><path d="M8 3.5l1.2 2.6h2.7l-2.2 1.7.9 2.7L8 9 5.4 10.5l.9-2.7-2.2-1.7h2.7z"/>',
    params: [
      {
        k: 'dataset',
        label: 'Dataset',
        type: 'select',
        opts: [
          { label: 'Match events', value: 'match_events.csv' },
          { label: 'Matches', value: 'matches.csv' },
          { label: 'Match details', value: 'matches_detailed.csv' },
          { label: 'Match lineups', value: 'match_lineups.csv' },
          { label: 'Team stats per match', value: 'match_team_stats.csv' },
          { label: 'Player stats', value: 'player_stats.csv' },
          { label: 'Squads & players', value: 'squads_and_players.csv' },
          { label: 'Teams', value: 'teams.csv' },
          { label: 'Venues', value: 'venues.csv' },
          { label: 'Tournament stages', value: 'tournament_stages.csv' },
          { label: 'Referees', value: 'referees.csv' },
        ],
        default: 'match_events.csv',
      },
    ],
    buildUrl: (params) =>
      `https://raw.githubusercontent.com/mominullptr/FIFA-World-Cup-2026-Dataset/refs/heads/main/${params.dataset ?? 'match_events.csv'}`,
    defaultUrl:
      'https://raw.githubusercontent.com/mominullptr/FIFA-World-Cup-2026-Dataset/refs/heads/main/match_events.csv',
    buildMeta: (params) => {
      const LABELS = {
        'match_events.csv': 'Match events',
        'matches.csv': 'Matches',
        'matches_detailed.csv': 'Match details',
        'match_lineups.csv': 'Match lineups',
        'match_team_stats.csv': 'Team stats per match',
        'player_stats.csv': 'Player stats',
        'squads_and_players.csv': 'Squads & players',
        'teams.csv': 'Teams',
        'venues.csv': 'Venues',
        'tournament_stages.csv': 'Tournament stages',
        'referees.csv': 'Referees',
      }
      const label = LABELS[params.dataset] ?? params.dataset ?? 'Match events'
      return { title: `FIFA World Cup 2026 — ${label}`, source: 'FIFA WC 2026 Dataset' }
    },
    matchesUrl: (url) =>
      /raw\.githubusercontent\.com\/mominullptr\/FIFA-World-Cup-2026-Dataset/.test(url),
    matchesPayload: () => false,
    normalize: (text) => text,
  },
  {
    id: 'open-meteo',
    label: 'Open-Meteo',
    description: 'Weather, climate & air quality',
    fmt: 'JSON',
    requiresKey: false,
    iconPaths: '<circle cx="8" cy="9" r="3.2"/><path d="M8 2.5v1.5M3 9H1.5M4 5l-1-1M12 5l1-1M8 14.5h7a3.5 3.5 0 000-7h-.3"/>',
    params: [
      {
        k: 'dataset',
        label: 'Dataset',
        type: 'select',
        opts: [
          { label: 'Forecast', value: 'forecast' },
          { label: 'Historical (ERA5)', value: 'historical' },
          { label: 'Air quality', value: 'air-quality' },
          { label: 'Marine', value: 'marine' },
        ],
        default: 'forecast',
      },
      {
        k: 'city',
        label: 'City',
        type: 'select',
        opts: ['Berlin', 'New York', 'San Francisco', 'London', 'Tokyo', 'Sydney'],
        default: 'Berlin',
      },
      {
        k: 'range',
        label: 'Date range',
        type: 'select',
        opts: ['Next 7 days', 'Next 14 days', 'Past 7 days'],
        default: 'Next 7 days',
      },
    ],
    buildUrl: (params) => {
      const { lat, lon } = OPEN_METEO_CITIES[params.city] ?? OPEN_METEO_CITIES['Berlin']
      const dataset = params.dataset ?? 'forecast'
      const BASE = {
        forecast:     'https://api.open-meteo.com/v1/forecast',
        historical:   'https://archive-api.open-meteo.com/v1/archive',
        'air-quality': 'https://air-quality-api.open-meteo.com/v1/air-quality',
        marine:       'https://marine-api.open-meteo.com/v1/marine',
      }
      const VARS = {
        forecast:     'temperature_2m,relative_humidity_2m,wind_speed_10m',
        historical:   'temperature_2m,relative_humidity_2m,precipitation',
        'air-quality': 'pm10,pm2_5,carbon_monoxide',
        marine:       'wave_height,wave_direction,wave_period',
      }
      const base = BASE[dataset] ?? BASE.forecast
      const vars = VARS[dataset] ?? VARS.forecast

      if (dataset === 'historical') {
        const today = new Date()
        const end = today.toISOString().split('T')[0]
        const daysBack = params.range === 'Next 14 days' ? 14 : 7
        const start = new Date(today.getTime() - daysBack * 86400000).toISOString().split('T')[0]
        return `${base}?latitude=${lat}&longitude=${lon}&start_date=${start}&end_date=${end}&hourly=${vars}&timezone=auto`
      }

      const rangeParam =
        params.range === 'Past 7 days' ? '&past_days=7' :
        params.range === 'Next 14 days' ? '&forecast_days=14' : ''
      return `${base}?latitude=${lat}&longitude=${lon}&hourly=${vars}&timezone=auto${rangeParam}`
    },
    defaultUrl:
      'https://api.open-meteo.com/v1/forecast?latitude=52.52&longitude=13.41&hourly=temperature_2m,relative_humidity_2m,wind_speed_10m&timezone=auto',
    buildMeta: (params) => {
      const DATASET_LABELS = {
        forecast: 'Forecast', historical: 'Historical (ERA5)',
        'air-quality': 'Air quality', marine: 'Marine',
      }
      const dataset = DATASET_LABELS[params.dataset ?? 'forecast'] ?? 'Forecast'
      return {
        title: `${params.city ?? 'Berlin'} — ${dataset}`,
        source: 'Open-Meteo',
      }
    },
    matchesUrl: (url) => /open-meteo\.com/i.test(url),
    matchesPayload: (payload) =>
      Boolean(payload?.hourly && Array.isArray(payload?.hourly?.time)),
    normalize: normalizeOpenMeteoPayload,
  },
  {
    id: 'census',
    label: 'U.S. Census Bureau',
    description: 'ACS table by geography',
    fmt: 'JSON',
    requiresKey: true,
    keyLabel: 'API key',
    keyDocs: 'https://api.census.gov/data/key_signup.html',
    iconPaths: '<path d="M2 14h12M3.5 14V6l4.5-3 4.5 3v8M6 14V10h4v4M6 8h.01M10 8h.01"/>',
    params: [
      {
        k: 'geo',
        label: 'Geography',
        type: 'select',
        opts: ['State', 'County', 'Census tract'],
        default: 'State',
      },
      {
        k: 'year',
        label: 'Year',
        type: 'select',
        opts: ['2024', '2022', '2021', '2020'],
        default: '2024',
      },
    ],
    buildUrl: (params) => {
      const forParam = {
        'State':        'state:*',
        'County':       'county:*&in=state:*',
        'Census tract': 'tract:*&in=state:*',
      }[params.geo] ?? 'state:*'
      return `https://api.census.gov/data/${params.year}/acs/acs5/profile?get=DP05_0001E,NAME&for=${forParam}`
    },
    defaultUrl:
      'https://api.census.gov/data/2024/acs/acs5/profile?get=DP05_0001E,NAME&for=state:*',
    buildMeta: (params) => ({
      title: `U.S. ${params.geo ?? 'State'} demographics (ACS ${params.year ?? '2024'})`,
      source: 'U.S. Census Bureau',
    }),
    matchesUrl: (url) => /api\.census\.gov/i.test(url),
    matchesPayload: (payload) =>
      Array.isArray(payload) && Array.isArray(payload[0]),
    normalize: normalizeCensusPayload,
  },
  {
    id: 'worldbank',
    label: 'World Bank',
    description: 'Economic & demographic indicators',
    fmt: 'JSON',
    requiresKey: false,
    dataPath: '[1]',
    iconPaths: '<path d="M2 12V7l3-3 3 2 3-4 3 1M2 12h12"/>',
    params: [
      {
        k: 'indicator',
        label: 'Indicator',
        type: 'select',
        opts: [
          { label: 'GDP (current US$)', value: 'NY.GDP.MKTP.CD' },
          { label: 'Population', value: 'SP.POP.TOTL' },
          { label: 'Life expectancy', value: 'SP.DYN.LE00.IN' },
          { label: 'CO₂ per capita', value: 'EN.GHG.CO2.PC.CE.AR5' },
        ],
        default: 'NY.GDP.MKTP.CD',
      },
      {
        k: 'country',
        label: 'Country (ISO code or "all")',
        type: 'text',
        default: 'all',
      },
    ],
    buildUrl: (params) =>
      buildUrlFromTemplate(
        'https://api.worldbank.org/v2/country/{country}/indicator/{indicator}?format=json&per_page=20000',
        params
      ),
    defaultUrl:
      'https://api.worldbank.org/v2/country/all/indicator/NY.GDP.MKTP.CD?format=json&per_page=20000',
    buildMeta: (params) => {
      const INDICATOR_LABELS = {
        'NY.GDP.MKTP.CD': 'GDP (current US$)',
        'SP.POP.TOTL': 'Population',
        'SP.DYN.LE00.IN': 'Life expectancy',
        'EN.GHG.CO2.PC.CE.AR5': 'CO₂ per capita',
      }
      const indicator = INDICATOR_LABELS[params.indicator] ?? params.indicator ?? 'Indicator'
      const country = params.country === 'all' ? 'All countries' : (params.country ?? 'All countries')
      return { title: `${indicator} — ${country}`, source: 'World Bank' }
    },
    matchesUrl: (url) => /api\.worldbank\.org/i.test(url),
    matchesPayload: (payload) =>
      Array.isArray(payload) && Array.isArray(payload[1]) && payload[1]?.[0]?.indicator != null,
    normalize: normalizeWorldBank,
  },
  {
    id: 'usgs',
    label: 'USGS',
    description: 'Earthquakes (real-time & historical)',
    fmt: 'GeoJSON',
    requiresKey: false,
    dataPath: 'features',
    iconPaths: '<path d="M1 9h2l2-4 1.5 7 2-9 2 8 1.5-3H15"/>',
    params: [
      {
        k: 'starttime',
        label: 'Start date',
        type: 'date',
        default: '2026-01-01',
      },
      {
        k: 'endtime',
        label: 'End date',
        type: 'date',
        default: 'today',
      },
      {
        k: 'minmagnitude',
        label: 'Min magnitude',
        type: 'number',
        default: 4,
      },
    ],
    buildUrl: (params) =>
      buildUrlFromTemplate(
        'https://earthquake.usgs.gov/fdsnws/event/1/query?format=geojson&starttime={starttime}&endtime={endtime}&minmagnitude={minmagnitude}',
        params
      ),
    defaultUrl: `https://earthquake.usgs.gov/fdsnws/event/1/query?format=geojson&starttime=2026-01-01&endtime=${new Date().toISOString().split('T')[0]}&minmagnitude=4`,
    buildMeta: (params) => ({
      title: `Earthquakes M${params.minmagnitude ?? 4}+ (${params.starttime ?? ''} – ${params.endtime ?? ''})`,
      source: 'USGS Earthquake Hazards Program',
    }),
    matchesUrl: (url) => /earthquake\.usgs\.gov/i.test(url),
    matchesPayload: (payload) =>
      payload?.type === 'FeatureCollection' && Array.isArray(payload?.features),
    normalize: normalizeUSGS,
  },
  {
    id: 'frankfurter',
    label: 'Frankfurter (ECB)',
    description: 'Exchange rates (ECB)',
    fmt: 'JSON',
    requiresKey: false,
    dataPath: 'rates',
    iconPaths: '<path d="M2 5h9M2 11h9"/><path d="M13 3.5l2 1.5-2 1.5M3 9.5L1 11l2 1.5"/>',
    params: [
      {
        k: 'base',
        label: 'Base currency',
        type: 'text',
        default: 'USD',
      },
      {
        k: 'start',
        label: 'Start date',
        type: 'date',
        default: '2024-01-01',
      },
      {
        k: 'end',
        label: 'End date',
        type: 'date',
        default: 'today',
      },
    ],
    buildUrl: (params) =>
      buildUrlFromTemplate(
        'https://api.frankfurter.dev/v1/{start}..{end}?base={base}',
        params
      ),
    defaultUrl: `https://api.frankfurter.dev/v1/2024-01-01..${new Date().toISOString().split('T')[0]}?base=USD`,
    buildMeta: (params) => ({
      title: `${params.base ?? 'USD'} exchange rates (${params.start ?? ''} – ${params.end ?? ''})`,
      source: 'Frankfurter / ECB',
    }),
    matchesUrl: (url) => /api\.frankfurter\.(dev|app)/i.test(url),
    matchesPayload: (payload) =>
      payload?.rates && payload?.base && typeof payload.rates === 'object' && !Array.isArray(payload),
    normalize: normalizeFrankfurter,
  },

]

export function getApiSourceById(id) {
  return API_SOURCES.find((source) => source.id === id) ?? null
}

export function detectApiSourceByUrl(url) {
  const normalizedUrl = typeof url === 'string' ? url.trim() : ''
  if (!normalizedUrl) {
    return null
  }

  return API_SOURCES.find((source) => source.matchesUrl?.(normalizedUrl)) ?? null
}

export function detectApiSourceByPayload(payload) {
  return API_SOURCES.find((source) => source.matchesPayload?.(payload)) ?? null
}

function appendApiKey(url, apiKey) {
  if (!apiKey) {
    return url
  }

  const trimmedUrl = typeof url === 'string' ? url.trim() : ''
  if (!trimmedUrl) {
    return trimmedUrl
  }

  const key = encodeURIComponent(apiKey)
  if (/([?&])key=/.test(trimmedUrl)) {
    return trimmedUrl.replace(/([?&])key=[^&]*/, `$1key=${key}`)
  }

  return `${trimmedUrl}${trimmedUrl.includes('?') ? '&' : '?'}key=${key}`
}

export function buildProxiedUrl(url) {
  // Route external URLs through the server-side proxy to avoid CORS issues.
  // Relative URLs and localhost are fetched directly.
  try {
    const parsed = new URL(url)
    const isLocal =
      parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1'
    if (!isLocal) {
      return `/api/proxy?url=${encodeURIComponent(url)}`
    }
  } catch {
    // relative URL — use as-is
  }
  return url
}

export async function loadUrlSourceData(source) {
  const url = typeof source?.url === 'string' ? source.url.trim() : ''
  if (!url) {
    throw new Error('Missing data URL.')
  }

  const response = await fetch(buildProxiedUrl(url))
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`)
  }

  const text = await response.text()
  const presetFromSelection = getApiSourceById(source?.apiSourceId)
  const presetFromUrl = detectApiSourceByUrl(url)
  let preset = presetFromSelection ?? presetFromUrl

  if (!preset) {
    try {
      const parsed = JSON.parse(text)
      preset = detectApiSourceByPayload(parsed)
      if (preset) {
        const rows = preset.normalize(parsed)
        const tsv = defaultNormalizeToTsv(rows)
        if (!tsv) {
          throw new Error('The API response did not contain a usable table.')
        }
        return tsv
      }
    } catch (error) {
      return text
    }
    return text
  }

  if (preset?.passThroughText) {
    return text
  }

  const userDataPath = source?.userDataPath
  const useGeneric = !!userDataPath && userDataPath !== (preset?.dataPath ?? '')

  try {
    const parsed = JSON.parse(text)

    let rows
    if (useGeneric || (!preset && userDataPath)) {
      const extracted = resolveDataPath(parsed, userDataPath)
      rows = genericFlatten(extracted)
    } else {
      rows = preset.normalize(parsed)
    }

    const tsv = defaultNormalizeToTsv(rows)
    if (!tsv) {
      throw new Error('The API response did not contain a usable table.')
    }
    return tsv
  } catch (error) {
    if (preset?.requiresKey) {
      throw new Error(
        `${preset.label} returned an unexpected response. Check the API key and URL.`
      )
    }
    throw error
  }
}

export function buildApiUrl(source, preset) {
  if (!preset?.requiresKey) {
    return source?.url?.trim() ?? ''
  }

  return appendApiKey(source?.url, source?.apiKey)
}

